import {Injectable} from '@angular/core';
import {
    FilterOutputModel,
    FiltersService, GeneralService,
    HttpService,
    SettingsProvider,
    TableResponseQuery
} from 'app/core';
import {TableHelperService} from 'app/shared/tables/table-helper.service';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {environment as env} from '../../../../environments/environment';
import {HttpParams} from '@angular/common/http';
import {filter, map, switchMap} from 'rxjs/operators';
import {ClusterModel} from '../models/cluster.model';
import {CurrentAssortmentListModel} from '../models/current-assortment-list.model';
import {AssortmentCategoriesModel} from '../models/assortment-categories.model';
import {AssortmentCategoriesDetailsModel} from '../models/assortment-categories-details.model';
import {CurrentAssortmentDetailsListModel} from '../models/current-assortment-details-list.model';
import {CurrentAssortmentEditProduct} from '../components/current-assortment-edit/current-assortment-edit.component';
import {AssortmentsTargetModel} from '../models/assortments-target.model';
import {Feature, ProductAttribute} from 'app/domain/features/services/features.data.service';
import {FeaturesListQuery} from 'app/domain/features/features-model';

@Injectable()
export class CurrentAssortmentService {

    private clustersReady = new BehaviorSubject(false);

    $clustersReady = this.clustersReady.asObservable();

    private clusters: ClusterModel[];

    constructor(
        private http: HttpService,
        private filterService: FiltersService,
        private tableHelperService: TableHelperService,
        private generalService: GeneralService,
        public settingsService: SettingsProvider,
    ) {
        this.fetchClustersData().subscribe();
    }

    fetchData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filterVal: string = '',
        selectFilters: FilterOutputModel[] = []
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.CurrentAssortmentService.apiUrlGetAssortmentsCategories}`;

        let params = new HttpParams();

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        params = params.set('$count', 'true');

        if (filterVal || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filterVal,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filterVal,
                    CurrentAssortmentListModel.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    CurrentAssortmentListModel.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.$clustersReady.pipe(
            filter((val) => val),
            switchMap(() => {
                return this.http.getWithParams(url, params).pipe(
                    map((response) => {
                        return new TableResponseQuery(
                            response.body.items,
                            response.body.count
                        );
                    }),
                    map((response) => {
                        const newItems = response.items.map((item: AssortmentCategoriesModel) => {
                            const newItem = {
                                Id: item.id,
                                categoryId: item.category2Id,
                                categoryName: this.generalService.getCategoryNameLevel(item, 'categoryNameLevel')
                            };

                            item.assortmentClusterNumbersList.forEach((tmpCluster) => {
                                const cluster: ClusterModel = this.clusters.find((cl) => cl.id === tmpCluster.assortmentClusterId);

                                if (cluster) {
                                    newItem['cluster' + cluster.id] = tmpCluster.assortmentClusterNumber;
                                } else {
                                    console.error('Not found Cluster');
                                }
                            });

                            return newItem;
                        });
                        return {
                            total_count: response.total_count,
                            items: newItems
                        };
                    })
                );
            }));
    }

    fetchDetailsData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filterVal: string = '',
        selectFilters: FilterOutputModel[] = [],
        categoryId: number
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.CurrentAssortmentService.apiUrlGetAssortmentsDetails}/${categoryId}/products`;

        let params = new HttpParams();

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        params = params.set('$count', 'true');

        if (filterVal || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filterVal,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filterVal,
                    CurrentAssortmentDetailsListModel.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    CurrentAssortmentDetailsListModel.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.$clustersReady.pipe(
            filter((val) => val),
            switchMap(() => {
                return this.http.getWithParams(url, params).pipe(
                    map((response) => {
                        return new TableResponseQuery(
                            response.body.items,
                            response.body.count
                        );
                    }),
                    map((response) => {
                        const newItems = response.items.map((item: AssortmentCategoriesDetailsModel) => {
                            const newItem = {
                                ...item
                            };

                            item.assortmentClusterStatusList.forEach((tmpCluster) => {
                                const cluster: ClusterModel = this.clusters.find((cl) => cl.id === tmpCluster.assortmentClusterId);

                                if (cluster) {
                                    newItem['cluster' + cluster.id] = tmpCluster.isActive;
                                } else {
                                    console.error('Not found Cluster');
                                }
                            });

                            return newItem;
                        });
                        return {
                            total_count: response.total_count,
                            items: newItems
                        };
                    })
                );
            }));
    }

    fetchClustersData(): Observable<TableResponseQuery<ClusterModel>> {
        if (this.clusters && this.clusters.length > 0) {
            return of(
                new TableResponseQuery(
                    this.clusters,
                    this.clusters.length
                )
            );
        } else {
            const url = `${this.settingsService.configuration.url}${env.CurrentAssortmentService.apiUrlGetAssortments}`;

            let params = new HttpParams();

            params = params.set('$count', 'true');

            return this.http.getWithParams(url, params).pipe(
                map((response) => {
                    this.clustersReady.next(true);
                    this.clusters = response.body.items;

                    return new TableResponseQuery(
                        response.body.items,
                        response.body.count
                    );
                })
            );
        }
    }

    getClusterDef(): ClusterModel[] {
        return this.clusters;
    }

    updateClusters(products: CurrentAssortmentEditProduct[]): Observable<any> {
        const url = `${this.settingsService.configuration.url}${env.CurrentAssortmentService.apiUrlPostAssortment}`;
        const data = {
            'ProductAssortmentClusterAssigns': products.map((product) => (
                {
                    ProductId: product.productId,
                    Clusters: product.clusters.map((cluster) => (
                        {
                            ProductAssortmentClusterId: cluster.id,
                            isActive: cluster.value
                        }
                    ))
                }
            ))
        };

        return this.http.post(url, data);
    }

    fetchAssortmentsTargetData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filterVal: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.PricesConfigDataService.apiUrlGetAssortmentsGoals}`;
        if (sortBy === 'categoryName asc' || sortBy === 'CategoryName asc') {
            sortBy = 'categoryNameLevel1 asc';
        }
        if (sortBy === 'categoryName desc' || sortBy === 'CategoryName desc') {
            sortBy = 'categoryNameLevel1 desc';
        }
        let params = new HttpParams();
        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        params = params.set('$count', 'true');

        // logic to set filter values
        if (filterVal || (selectFilters && !!selectFilters.length)) {
            const mainFilterString = this.filterService.setFilters(
                filterVal,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filterVal,
                    CurrentAssortmentListModel.tableInfo().columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    CurrentAssortmentListModel.tableInfo().columns
                )
            );
            params = params.set('$filter', mainFilterString);
        }

        return this.$clustersReady.pipe(
            filter((val) => val),
            switchMap(() => {
                return this.http
                    .getWithParams(url, params)
                    .pipe(
                        map((response) => {
                            return new TableResponseQuery(
                                response.body.items,
                                response.body.count
                            );
                        }),
                        map((response) => {
                            const newItems = response.items.map((item: AssortmentsTargetModel) => {
                                const newItem = {
                                    Id: item.id,
                                    categoryId: item.category2Id,
                                    categoryName: this.generalService.getCategoryNameLevel(item, 'categoryNameLevel')
                                };

                                item.clusterGoals.forEach((tmpCluster) => {
                                    const cluster: ClusterModel =
                                        this.clusters.find(
                                            (cl) => cl.id === tmpCluster.productAssortmentClusterId
                                        );

                                    if (cluster) {
                                        newItem['cluster' + cluster.id] = tmpCluster.quantity;
                                    } else {
                                        console.error('Not found Cluster');
                                    }
                                });

                                return newItem;
                            });

                            return {
                                total_count: response.total_count,
                                items: newItems
                            };
                        })
                    );
            }));
    }

    getAssortmentTargetById(id: number): Observable<{categoryId: number, categoryName: string}> {
        const url = `${this.settingsService.configuration.url}${env.PricesConfigDataService.apiUrlGetAssortmentsGoals}`;

        return this.$clustersReady.pipe(
            filter((val) => val),
            switchMap(() => {
                return this.http.get(
                    url + `?$filter=(categoryId eq ${id})`
                ).pipe(
                    map((response) => {
                        const newItems = response.items.map((item: AssortmentsTargetModel) => {
                            const newItem = {
                                Id: item.id,
                                categoryId: item.category1Id,
                                categoryName: item.category1Name + ' / ' + item.category2Name
                            };

                            item.clusterGoals.forEach((tmpCluster) => {
                                const cluster: ClusterModel = this.clusters.find((cl) => cl.id === tmpCluster.productAssortmentClusterId);

                                if (cluster) {
                                    newItem['cluster' + cluster.id] = tmpCluster.quantity;
                                } else {
                                    console.error('Not found Cluster');
                                }
                            });

                            return newItem;
                        });

                        return newItems[0] || null;
                    })
                );
            })
        );
    }
}
